import { useCallback, useMemo, useState } from "react";
import { DndContext } from "@dnd-kit/core";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTankPlanningState } from "@/hooks/useTankPlanningState";
import { useClientTankSchedule } from "@/hooks/useClientTankSchedule";
import { useMyPortalClients } from "@/hooks/usePortalClientAccess";
import { useVarietyColorMap, useVarietyNameMap } from "@/hooks/useVarietyColorMap";
import {
  computeTransferConnections,
  getLotForAssignment,
  type VesselAssignmentWithRelations,
} from "@/hooks/useVesselAssignments";
import { TANK_SUBTYPES } from "@/lib/vessel-types";
import { sortPlannerVessels } from "@/lib/tank-planning-sort";
import { TimelineControls } from "./TimelineControls";
import { TankPlanningTimeline } from "./TankPlanningTimeline";
import { OnDeckSidebar } from "./OnDeckSidebar";

/**
 * Customer-portal Tank Schedule (Phase 1: read-only). Reuses the staff
 * planner's grid with readOnly on; data comes from get_client_tank_schedule,
 * which masks other clients' bars server-side (is_mine = false → grey
 * "Occupied"). See reqs/34-client-tank-planning.md.
 */
export function ClientTankPlanningView() {
  const colorMap = useVarietyColorMap();
  const nameMap = useVarietyNameMap();
  const { data: myClients = [] } = useMyPortalClients();

  const {
    currentDate,
    zoomLevel,
    dateRange,
    dayWidth,
    vesselTypeFilter,
    setZoomLevel,
    setVesselTypeFilter,
    navigate,
    extendEnd,
    extendStart,
    goToDate,
  } = useTankPlanningState();

  const { data, isLoading, isFetching } = useClientTankSchedule(dateRange.start, dateRange.end);

  const [facilityFilter, setFacilityFilter] = useState("all");
  const [brandFilter, setBrandFilter] = useState("all");
  const [myTanksOnly, setMyTanksOnly] = useState(false);
  const [onDeckExpanded, setOnDeckExpanded] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // A bar is shown in full when it's the client's own and matches the brand filter.
  // The client's other brands' bars are hidden (not masked) while a brand is selected.
  const matchesBrand = useCallback(
    (a: VesselAssignmentWithRelations) =>
      brandFilter === "all" || getLotForAssignment(a)?.client?.id === brandFilter,
    [brandFilter],
  );

  const assignments = useMemo(
    () => (data?.assignments ?? []).filter((a) => !a.is_mine || matchesBrand(a)),
    [data?.assignments, matchesBrand],
  );

  const onDeck = useMemo(() => (data?.onDeck ?? []).filter(matchesBrand), [data?.onDeck, matchesBrand]);

  const facilityOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const v of data?.vessels ?? []) {
      if (v.facility) seen.set(v.facility.id, v.facility.code);
    }
    return [...seen].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
  }, [data?.vessels]);

  const vessels = useMemo(() => {
    let filtered = data?.vessels ?? [];
    if (vesselTypeFilter !== "all") {
      filtered = filtered.filter((v) => (v.vessel_subtypes || []).includes(vesselTypeFilter));
    }
    if (facilityFilter !== "all") {
      filtered = filtered.filter((v) => v.facility_id === facilityFilter);
    }
    if (myTanksOnly) {
      const mine = new Set(assignments.filter((a) => a.is_mine).map((a) => a.vessel_id));
      filtered = filtered.filter((v) => mine.has(v.id));
    }
    return sortPlannerVessels(filtered);
  }, [data?.vessels, vesselTypeFilter, facilityFilter, myTanksOnly, assignments]);

  const transferConnections = useMemo(
    () => computeTransferConnections(assignments, data?.connections ?? []),
    [assignments, data?.connections],
  );

  const vesselTypes = useMemo(() => [...TANK_SUBTYPES] as { value: string; label: string }[], []);

  const extraControls = (
    <>
      {myClients.length > 1 && (
        <Select value={brandFilter} onValueChange={setBrandFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All brands" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All brands</SelectItem>
            {myClients.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      <Button
        variant={myTanksOnly ? "secondary" : "outline"}
        size="sm"
        onClick={() => setMyTanksOnly((v) => !v)}
        aria-pressed={myTanksOnly}
      >
        My tanks
      </Button>
    </>
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      <TimelineControls
        currentDate={currentDate}
        zoomLevel={zoomLevel}
        vesselTypeFilter={vesselTypeFilter}
        facilityFilter={facilityFilter}
        onNavigate={navigate}
        onZoomChange={setZoomLevel}
        onFilterChange={setVesselTypeFilter}
        onFacilityFilterChange={setFacilityFilter}
        onGoToDate={goToDate}
        vesselTypes={vesselTypes}
        facilities={facilityOptions}
        extraControls={extraControls}
        hideHistory
        hidePresence
      />

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 py-2 border-b bg-card text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-5 rounded-sm"
            style={{ background: "linear-gradient(90deg, #8b2252 0 33%, #d4a017 33% 66%, #6b8e23 66%)" }}
          />
          Your wine (coloured by varietal)
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-5 rounded-sm"
            style={{
              background:
                "repeating-linear-gradient(-45deg, hsl(var(--primary) / 0.7) 0px, hsl(var(--primary) / 0.7) 3px, hsl(var(--primary) / 0.45) 3px, hsl(var(--primary) / 0.45) 6px)",
            }}
          />
          Planned, not yet received
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-3 w-5 rounded-sm bg-muted-foreground/30" />
          Occupied
        </span>
        <span className="ml-auto">View only. Contact the winery to request changes.</span>
      </div>

      {/* Grid is read-only, but bars and rows use dnd-kit hooks, so they need a context. */}
      <DndContext>
        <div className="flex-1 min-h-0 flex overflow-hidden">
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden relative">
            {isFetching && !isLoading && (
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary/20 z-50 overflow-hidden">
                <div className="h-full w-1/3 bg-primary animate-[shimmer_1s_ease-in-out_infinite]" />
              </div>
            )}
            <TankPlanningTimeline
              vessels={vessels}
              assignments={assignments}
              transferConnections={transferConnections}
              startDate={dateRange.start}
              endDate={dateRange.end}
              zoomLevel={zoomLevel}
              dayWidth={dayWidth}
              isLoading={isLoading && !data}
              onAssignmentSelect={(a) => setSelectedId(a.is_mine ? a.id : null)}
              onAssignmentEditRequest={() => {}}
              onDeselect={() => setSelectedId(null)}
              selectedAssignmentId={selectedId}
              onScrollNearEnd={extendEnd}
              onScrollNearStart={extendStart}
              colorMap={colorMap}
              nameMap={nameMap}
              readOnly
              showNotes
              emptyMessage={
                myTanksOnly
                  ? { title: "None of your wine is in a tank here", detail: "Turn off “My tanks” to see the whole cellar" }
                  : { title: "No tanks to show", detail: "Ask the winery if you expected to see tanks here" }
              }
            />
          </div>
          <OnDeckSidebar
            expanded={onDeckExpanded}
            onToggle={() => setOnDeckExpanded((v) => !v)}
            assignments={onDeck}
            isLoading={isLoading && !data}
            readOnly
          />
        </div>
      </DndContext>
    </div>
  );
}
